import { isAxiosError } from 'axios'
import axios from '../axios'
import { AppError, BusinessError, ConflictError, NotFoundError } from '../errorTypes'
import { isApiError, type ApiResponse } from '../types'
import type {
  SettingsRepository,
  SettingsWriteOptions
} from '@trading/core/src/repositories/settings/settings.repository'
import type { SettingsListModel, SettingsPayload, SettingsRecord } from '@trading/core'

function unwrap<T>(envelope: ApiResponse<T>): T {
  if (!envelope.success) {
    throw new BusinessError(
      isApiError(envelope.error) ? envelope.error.message : 'Settings request failed'
    )
  }
  return envelope.data
}

// 409 и 413 общий перехватчик не разбирает: на них отвечают только эндпоинты настроек
function toSettingsError(error: unknown): unknown {
  if (error instanceof AppError || !isAxiosError<ApiResponse<never>>(error)) return error

  const status = error.response?.status
  const body = error.response?.data

  if (status === 409) {
    const apiError = body && !body.success ? body.error : null
    return isApiError(apiError)
      ? new ConflictError(apiError.message, apiError.details)
      : new ConflictError()
  }

  if (status === 413) {
    return new BusinessError('Settings payload is too large')
  }

  return error
}

function scopeUrl(scope: string): string {
  return `/user/settings/${encodeURIComponent(scope)}`
}

export class SettingsRepositoryAxios implements SettingsRepository {
  constructor(private apiClient = axios) {
  }

  async getAll(): Promise<SettingsRecord[]> {
    try {
      const resp = await this.apiClient.get<ApiResponse<SettingsListModel>>('/user/settings')
      return unwrap(resp.data).settings
    } catch (error) {
      throw toSettingsError(error)
    }
  }

  async getScope<T extends SettingsPayload = SettingsPayload>(
    scope: string
  ): Promise<SettingsRecord<T> | null> {
    try {
      const resp = await this.apiClient.get<ApiResponse<SettingsRecord<T>>>(scopeUrl(scope))
      return unwrap(resp.data)
    } catch (error) {
      if (error instanceof NotFoundError) return null
      throw toSettingsError(error)
    }
  }

  async putScope<T extends SettingsPayload = SettingsPayload>(
    scope: string,
    payload: T,
    options?: SettingsWriteOptions
  ): Promise<SettingsRecord<T>> {
    try {
      const resp = await this.apiClient.put<ApiResponse<SettingsRecord<T>>>(
        scopeUrl(scope),
        { payload },
        options?.ifMatchVersion != null
          ? { headers: { 'If-Match': String(options.ifMatchVersion) } }
          : undefined
      )
      return unwrap(resp.data)
    } catch (error) {
      throw toSettingsError(error)
    }
  }

  async patchScope<T extends SettingsPayload = SettingsPayload>(
    scope: string,
    payload: Partial<T>
  ): Promise<SettingsRecord<T>> {
    try {
      const resp = await this.apiClient.patch<ApiResponse<SettingsRecord<T>>>(
        scopeUrl(scope),
        { payload }
      )
      return unwrap(resp.data)
    } catch (error) {
      throw toSettingsError(error)
    }
  }

  async deleteScope(scope: string): Promise<void> {
    try {
      const resp = await this.apiClient.delete<ApiResponse<{ scope: string, deleted: boolean }>>(
        scopeUrl(scope)
      )
      unwrap(resp.data)
    } catch (error) {
      if (error instanceof NotFoundError) return
      throw toSettingsError(error)
    }
  }
}
